import { useMemo, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { keepPreviousData, useQuery } from '@tanstack/react-query'
import { api } from '@/api/client'
import { usePageLoader } from '@/components/LogoLoader'
import { Select } from '@/components/Select'
import { SortableTable, type Column } from '@/components/SortableTable'
import { StatTile } from '@/components/StatTile'
import { usePersistedState } from '@/lib/usePersistedState'
import { useIsPhone } from '@/lib/useMediaQuery'
import { fmtDate, useT } from '@/i18n'
import { commonMessages } from '@/i18n/messages/common'
import { crewsMessages } from '@/i18n/messages/crews'
import { Badge } from './Badge'
import { EditCrewDialog } from './EditCrewDialog'
import { WINDOWS, needsAttention, type CrewListItem } from './types'

/**
 * Crews (kolone) as AU records them: leader, Bauleiter, who is really checked in, and where AU's
 * own records disagree with that. Identity edits overlay AU; attendance stays derived.
 */
export function CrewsPage() {
  const t = useT(crewsMessages)
  const tc = useT(commonMessages)
  const phone = useIsPhone()
  const navigate = useNavigate()
  const [days, setDays] = usePersistedState<number>('crews:days', 30)
  const [activeOnly, setActiveOnly] = usePersistedState('crews:activeOnly', true)
  const [q, setQ] = useState('')
  const [editing, setEditing] = useState<CrewListItem | null>(null)

  const { data, isPending, error } = useQuery<CrewListItem[]>({
    queryKey: ['crews', days],
    queryFn: () => api.get<CrewListItem[]>(`/api/crews/?days=${days}`),
    placeholderData: keepPreviousData,
  })

  const all = data ?? []
  const rows = useMemo(() => {
    const needle = q.trim().toLowerCase()
    return all.filter((c) =>
      (!activeOnly || c.active) &&
      (!needle || [c.name, c.code, c.company, c.leader, c.bauleiter].some((v) => v?.toLowerCase().includes(needle))))
  }, [all, q, activeOnly])

  const active = all.filter((c) => c.active)
  const people = active.reduce((s, c) => s + c.members, 0)
  const attention = active.filter(needsAttention).length

  const loader = usePageLoader(isPending, t('loadingCrews'))
  if (loader) return loader
  if (error) return <p className="text-sm" style={{ color: 'var(--viz-critical)' }}>{tc('couldNotLoad', { what: t('title') })}</p>

  const columns: Column<CrewListItem>[] = [
    {
      key: 'crew', header: t('colCrew'),
      className: 'min-w-0',
      sortValue: (c) => (c.name ?? '').toLowerCase(),
      render: (c) => (
        <div className="min-w-0">
          <Link to={`/crews/${c.crewId}`} className="font-medium hover:underline">{c.name ?? '—'}</Link>
          {(c.code || c.company) && (
            <div className="truncate text-xs text-neutral-400">{[c.code, c.company].filter(Boolean).join(' · ')}</div>
          )}
          {(c.leader || c.bauleiter) && (
            <div className="truncate text-xs text-neutral-500">
              {[c.leader, c.bauleiter].filter(Boolean).join(' · ')}
            </div>
          )}
        </div>
      ),
    },
    {
      key: 'headcount', header: t('colHeadcount'), align: 'right',
      className: 'w-24',
      sortValue: (c) => c.members,
      render: (c) => <span className="tabular whitespace-nowrap">{c.members} / {c.roster}</span>,
    },
    {
      key: 'last', header: t('colLastCheckIn'),
      className: 'w-28',
      sortValue: (c) => c.lastCheckIn ?? '',
      render: (c) => <span className="tabular whitespace-nowrap">{fmtDate(c.lastCheckIn)}</span>,
    },
    {
      key: 'checks', header: t('colChecks'),
      className: 'w-28',
      render: (c) => <Warnings crew={c} compact />,
    },
    {
      key: 'edit', header: '',
      sticky: 'right',
      className: 'w-16 text-right',
      render: (c) => (
        <button
          type="button"
          onClick={() => setEditing(c)}
          className="rounded-md px-2 py-1 text-xs font-medium text-brand-700 ring-1 ring-brand-600/30 hover:bg-brand-50 dark:text-brand-100 dark:ring-brand-100/20 dark:hover:bg-brand-700/20"
        >
          {tc('edit')}
        </button>
      ),
    },
  ]

  return (
    <div className="space-y-4">
      <header>
        <h1 className="text-2xl font-bold tracking-tight">{t('title')}</h1>
        <p className="mt-1 text-sm text-neutral-500">{t('subtitle')}</p>
      </header>

      <div className="grid grid-cols-3 gap-2 sm:gap-3">
        <StatTile label={t('statActive')} value={active.length} unit="" />
        <StatTile label={t('statPeople')} value={people} unit="" />
        <StatTile label={t('statAttention')} value={attention} unit="" />
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <input
          type="search"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder={t('searchPlaceholder')}
          aria-label={t('searchLabel')}
          className="min-w-0 basis-full rounded-xl bg-white px-3 py-2 text-sm ring-1 ring-neutral-200 outline-none focus:ring-brand-500 sm:basis-80 dark:bg-neutral-900 dark:ring-neutral-800"
        />
        <label className="flex items-center gap-2 text-sm">
          <input type="checkbox" checked={activeOnly} onChange={(e) => setActiveOnly(e.target.checked)} />
          {t('activeOnly')}
        </label>
        <label className="flex items-center gap-2 text-sm">
          <span className="text-neutral-500">{t('window')}</span>
          <Select
            value={String(days)}
            onChange={(v) => setDays(Number(v))}
            className="w-fit min-w-[8rem]"
            options={WINDOWS.map((d) => ({ value: String(d), label: t('lastDays', { days: d }) }))}
          />
        </label>
        <span className="ml-auto text-xs text-neutral-500">{t('shownOf', { shown: rows.length, total: all.length })}</span>
      </div>

      {phone ? (
        <ul className="space-y-2">
          {rows.length === 0 && <li className="text-sm text-neutral-500">{t('noCrews')}</li>}
          {[...rows].sort((a, b) => b.members - a.members).map((c) => (
            <li key={c.crewId} className="rounded-2xl bg-white p-3 ring-1 ring-neutral-200 dark:bg-neutral-900 dark:ring-neutral-800">
              <div className="flex items-start justify-between gap-2">
                <button
                  type="button"
                  onClick={() => navigate(`/crews/${c.crewId}`)}
                  className="min-w-0 flex-1 text-left"
                >
                  <div className="flex items-baseline justify-between gap-2">
                    <span className="font-medium">{c.name ?? '—'}</span>
                    <span className="tabular text-xs text-neutral-500">{c.members} / {c.roster} · {fmtDate(c.lastCheckIn)}</span>
                  </div>
                  <div className="mt-0.5 text-xs text-neutral-500">
                    {[c.company, c.leader, c.bauleiter].filter(Boolean).join(' · ')}
                  </div>
                </button>
                <button
                  type="button"
                  onClick={() => setEditing(c)}
                  className="shrink-0 rounded-md px-2 py-1 text-xs font-medium text-brand-700 ring-1 ring-brand-600/30 hover:bg-brand-50 dark:text-brand-100 dark:ring-brand-100/20 dark:hover:bg-brand-700/20"
                >
                  {tc('edit')}
                </button>
              </div>
              <div className="mt-1.5"><Warnings crew={c} /></div>
            </li>
          ))}
        </ul>
      ) : (
        <SortableTable
          columns={columns}
          rows={rows}
          rowKey={(c) => c.crewId}
          initialSort={{ key: 'headcount', dir: 'desc' }}
          empty={t('noCrews')}
          fixed
        />
      )}

      <p className="text-xs text-neutral-400">{t('readOnlyNote')}</p>

      {editing && <EditCrewDialog crew={editing} onClose={() => setEditing(null)} />}
    </div>
  )
}

function Warnings({ crew: c, compact }: { crew: CrewListItem; compact?: boolean }) {
  const t = useT(crewsMessages)
  if (!c.active) return <Badge tone="grey">{t('inactive')}</Badge>
  if (!needsAttention(c)) return <Badge tone="green">{t('active')}</Badge>
  if (compact) {
    return (
      <div className="flex flex-wrap gap-1">
        {c.leaderNotCheckedIn && <Badge tone="red" title={t('warnLeaderNotCheckedIn')}>{t('colLeader')}</Badge>}
        {c.notOnRoster > 0 && <Badge tone="amber" title={t('warnNotOnRoster', { count: c.notOnRoster })}>+{c.notOnRoster}</Badge>}
        {c.rosterLeft > 0 && <Badge tone="amber" title={t('warnRosterLeft', { count: c.rosterLeft })}>−{c.rosterLeft}</Badge>}
        {c.bauleiterOutdatedInAu && <Badge tone="grey" title={t('warnBauleiterOutdated')}>BL</Badge>}
      </div>
    )
  }
  return (
    <div className="flex flex-wrap gap-1">
      {c.leaderNotCheckedIn && <Badge tone="red">{t('warnLeaderNotCheckedIn')}</Badge>}
      {c.notOnRoster > 0 && <Badge tone="amber">{t('warnNotOnRoster', { count: c.notOnRoster })}</Badge>}
      {c.rosterLeft > 0 && <Badge tone="amber">{t('warnRosterLeft', { count: c.rosterLeft })}</Badge>}
      {c.bauleiterOutdatedInAu && <Badge tone="grey" title={t('warnBauleiterOutdated')}>{t('colBauleiter')} ⚠</Badge>}
    </div>
  )
}
